import {API} from "@/api/generated";
import {defineStore} from "pinia";
import {getUtm} from "@/scripts/general";
import {getDoctorPhoto, getMiniPhoto, getMonth} from "@/widget/ScriptsWidget";
import device from "current-device";
import type {stateWidget, setWidget} from "@/widget/WidgetTypes";
import type {doctor} from "@/page/onlineAppointment/doctors/DoctorsType";
import {addMetrik} from "@/scripts/metrik";
import {clinic} from "@/page/onlineAppointment/clinic/ClinicTypes";

function cloneHint(
  hint: stateWidget["stages"][number]
): stateWidget["stages"][number] {
  return [...hint];
}

function sortSpecialization(
  clinic: Partial<clinic>,
  a: doctor,
  b: doctor
): number {
  if (!clinic.specializationList) {
    return 0;
  }
  let indexA = clinic.specializationList.findIndex(
    (s) => s == a.specialization[0]
  );
  let indexB = clinic.specializationList.findIndex(
    (s) => s == b.specialization[0]
  );
  if (indexA > indexB) {
    return 1;
  }
  if (indexA < indexB) {
    return -1;
  }
  return 0;
}

function sortDataName(a: doctor, b: doctor): number {
  if (a.name > b.name) {
    return 1;
  }
  if (a.name < b.name) {
    return -1;
  }
  return 0;
}
function sortData(dataList: doctor[], clinic: Partial<clinic>): doctor[] {
  if (dataList == null) {
    return [];
  }
  let dataTrue = dataList.filter((dataItem) => {
    if (!dataItem) {
      return false;
    }

    return dataItem.status;
  });
  dataTrue = dataTrue
    ?.sort(sortDataName)
    .sort((a, b) => sortSpecialization(clinic, a, b));
  return dataTrue;
}
const hints: stateWidget["hints"] = {
  service: ["1", "Выберете врача или услугу"],
  doctor: ["1", "Выберете врача или услугу"],
  dateTime: ["3", "Выберете дату и время"],
  shape: ["4", "Укажите ваши контактные данные"]
};
const {href, search} = window.location;
const webWersion = href.includes("readds.ru") && search.includes("id=");

export const useWidgetStore = defineStore("widgetState", {
  state: (): stateWidget => ({
    hints,
    doctors: [],
    clinic: {},
    imgDoctor: [],
    sendStatus: "",
    chose: {
      _id: "",
      ClientPhone: "",
      ClientFullName: "",
      birthdate: "",
      doctor: {},
      identIntegrationKey: [],
      DoctorService: "",
      DoctorName: "",
      comment: "",
      deviceType: "",
      DateTime: "",
      PlanStart: "",
      Utm: "",
      UserId: "",
      Name: "",
      URL: ""
    },
    selectedStage: 1,
    navWidget: "УСЛУГИ",
    stages: [cloneHint(hints["service"])],
    selectedMonthStr: "",
    selectedDayStr: "",
    selectedTime: "",
    appointment: false,
    consultation: false,
    key: ""
  }),

  actions: {
    async getWidgetData(auth: boolean): Promise<void> {
      const data = auth
        ? await API.getWidgetDataAuth()
        : await API.getWidgetData({query: {id: this.key}});
      this.setDatasInWidget(data);
    },
    async setChose(name: string): Promise<void> {
      this.setSendStatus("Loading");
      let deviceType = "не определено";
      switch (device.type) {
        case "desktop":
          deviceType = "Компьютер";
          break;
        case "mobile":
          deviceType = "Телефон";
          break;
        case "tablet":
          deviceType = "Планшет";
          break;
      }
      if ("identIntegrationKey" in this.clinic) {
        this.chose.identIntegrationKey = this.clinic.identIntegrationKey;
      }
      this.chose.deviceType = deviceType;
      let today = new Date().toLocaleString();
      let [date, time] = today.split(",");
      time = time.substring(0, 6);
      this.chose.DateTime = date + time;
      const mounthYear = this.selectedMonthStr.split(" ");
      if (
        this.selectedDayStr &&
        getMonth(mounthYear[0]) &&
        mounthYear[1] &&
        this.selectedTime
      ) {
        let PlanStart =
          this.selectedDayStr +
          "." +
          getMonth(mounthYear[0]) +
          "." +
          mounthYear[1] +
          " " +
          this.selectedTime;
        this.chose.PlanStart = PlanStart;
      }
      this.chose["Utm"] = getUtm();
      this.chose["UserId"] = this.key;
      this.chose["Name"] = name;
      this.chose["URL"] = window.location.href;
      await API.setTicket({body: {chose: this.chose}})
        .then(() => {
          this.appointment = true;
          const clinic = this.clinic;
          if (clinic.metricIntegrationKey) {
            clinic.metricIntegrationKey.forEach((element: {id: string}) => {
              ym(Number(element.id), "reachGoal", "ReaddsApplication");
            });
          }
          this.setSendStatus("Success");
        })
        .catch((error) => {
          this.setSendStatus("Error");
          throw error;
        })
        .finally(() => {
          setTimeout(() => {
            this.setSendStatus("");
          }, 1000);
        });
    },

    resetCalendar(): void {
      this.selectedMonthStr = "";
      this.selectedDayStr = "";
      this.selectedTime = "";
    },
    reset(): void {
      this.chose = {
        _id: "",
        ClientPhone: "",
        ClientFullName: "",
        birthdate: "",
        doctor: {},
        identIntegrationKey: [],
        DoctorService: "",
        DoctorName: "",
        comment: "",
        deviceType: "",
        DateTime: "",
        PlanStart: "",
        Utm: "",
        UserId: "",
        Name: "",
        URL: ""
      };
      this.selectedStage = 1;
      this.stages = [cloneHint(hints["service"])];
      this.selectedMonthStr = "";
      this.selectedDayStr = "";
      this.selectedTime = "";
      this.appointment = false;
      this.consultation = false;
    },
    setStages(data: stateWidget["stages"]): void {
      this.stages = data.map(cloneHint);
    },
    setKey(key: string): void {
      this.key = key;
    },
    updateConsultation(data: boolean): void {
      this.consultation = data;
    },
    updateAppointment(data: boolean): void {
      this.appointment = data;
    },
    setDatasInWidget(data: setWidget): void {
      const clinic: Partial<clinic> = data.clinic;
      this.clinic = clinic;

      if (
        webWersion &&
        "metricIntegrationKey" in clinic &&
        clinic.metricIntegrationKey
      ) {
        addMetrik(clinic.metricIntegrationKey.map((m) => Number(m.id)));
      }
      this.doctors = sortData(data.doctors, clinic);
      this.imgDoctor = [];
      if (this.doctors.length > 0) {
        for (let index = 0; index < 3; index++) {
          const doctor = this.doctors[index % this.doctors.length];
          this.imgDoctor.push(
            getMiniPhoto(getDoctorPhoto(doctor?.photo), "vrach.svg")
          );
        }
      }
      if (webWersion && this.clinic.clinic) {
        document.title = this.clinic.clinic;
        const iconElement = document.querySelector(
          "[rel=icon]"
        ) as HTMLElement | null;
        if (iconElement && "href" in iconElement) {
          iconElement.href = "";
        }
      }
    },
    setSelectedStage(key: number): void {
      this.selectedStage = key;
    },
    setNavWidget(data: string): void {
      this.navWidget = data;
    },
    setSelectedMonthStr(data: string): void {
      this.selectedMonthStr = data;
    },
    setSelectedDayStr(data: string): void {
      this.selectedDayStr = data;
    },
    setSelectedTime(data: string): void {
      this.selectedTime = data;
    },
    setSendStatus(status: stateWidget["sendStatus"]): void {
      this.sendStatus = status;
    }
  },
  getters: {
    getSendStatus(state: stateWidget): stateWidget["sendStatus"] {
      return state.sendStatus;
    },
    getKey(state: stateWidget): stateWidget["key"] {
      return state.key;
    },
    getAppointment(state: stateWidget): stateWidget["appointment"] {
      return state.appointment;
    },
    getConsultation(state: stateWidget): stateWidget["consultation"] {
      return state.consultation;
    },
    getDoctors(state: stateWidget): stateWidget["doctors"] {
      return state.doctors;
    },
    getImgDoctor(state: stateWidget): stateWidget["imgDoctor"] {
      return state.imgDoctor;
    },
    getChose(state: stateWidget): stateWidget["chose"] {
      return state.chose;
    },
    getSelectedStage(state: stateWidget): stateWidget["selectedStage"] {
      return state.selectedStage;
    },
    getNavWidget(state: stateWidget): stateWidget["navWidget"] {
      return state.navWidget;
    },
    getStages(state: stateWidget): stateWidget["stages"] {
      return state.stages;
    },
    getHints(state: stateWidget): stateWidget["hints"] {
      return state.hints;
    },
    getSelectedMonthStr(state: stateWidget): stateWidget["selectedMonthStr"] {
      return state.selectedMonthStr;
    },
    getSelectedDayStr(state: stateWidget): stateWidget["selectedDayStr"] {
      return state.selectedDayStr;
    },
    getSelectedTime(state: stateWidget): stateWidget["selectedTime"] {
      return state.selectedTime;
    }
  }
});
